import React, { useEffect, useRef, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { useAuth } from '../../contexts/AuthContext';
import { integrationsEnabled } from '../../lib/integrations';
import { AI_PROVIDERS, getAIConnections, connectAIProvider, disconnectAIProvider } from '../../services/ollama';

const KEY_LINKS = { openai: 'https://platform.openai.com/api-keys', anthropic: 'https://platform.claude.com/settings/keys', gemini: 'https://aistudio.google.com/api-keys' };
export default function AIConnectionDialog({ open, onClose, initialProvider = 'openai' }) {
  const { user } = useAuth();
  const [provider, setProvider] = useState(initialProvider === 'ollama' ? 'openai' : initialProvider);
  const [apiKey, setApiKey] = useState('');
  const [connections, setConnections] = useState({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const form = useRef(null);
  useEffect(() => {
    if (!open) return;
    const previous = document.activeElement;
    const elements = () => [...(form.current?.querySelectorAll('button:not(:disabled), input:not(:disabled), select:not(:disabled), a[href]') || [])];
    elements()[0]?.focus();
    const trap = event => {
      if (event.key !== 'Tab') return;
      const controls = elements(); const first = controls[0]; const last = controls[controls.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
    };
    document.addEventListener('keydown', trap);
    return () => { document.removeEventListener('keydown', trap); previous?.focus?.(); };
  }, [open]);
  useEffect(() => {
    setApiKey(''); setError(''); setMessage(''); setConnections({});
    setProvider(initialProvider === 'ollama' ? 'openai' : initialProvider);
    if (!open || !integrationsEnabled) return;
    let active = true;
    getAIConnections().then(result => { if (active) setConnections(result.connections); }).catch(e => { if (active) setError(e.message); });
    return () => { active = false; };
  }, [open, user?.id, initialProvider]);
  const run = async work => {
    setBusy(true); setError(''); setMessage('');
    try {
      await work();
      setApiKey('');
      setConnections((await getAIConnections()).connections);
      window.dispatchEvent(new Event('spcc:ai-connections-changed'));
    } catch (e) { setError(e.message); }
    finally { setBusy(false); }
  };
  const connected = connections[provider];
  return <Dialog open={open} onOpenChange={() => { if (!busy) { setApiKey(''); onClose(); } }}>
    <DialogContent aria-label="Connect your AI provider" style={{ maxWidth: 520 }}>
      <DialogHeader><DialogTitle>Connect your AI provider</DialogTitle><DialogDescription>Use your own provider API key. This connection belongs to your signed-in SPCC account.</DialogDescription></DialogHeader>
      <form ref={form} style={{ display: 'grid', gap: 12 }} onSubmit={e => { e.preventDefault(); run(async () => { await connectAIProvider(provider, apiKey); setMessage(`${AI_PROVIDERS[provider]} connected. Choose a model to start using it.`); }); }}>
        <label className="v2-field"><span>AI provider</span><select value={provider} disabled={busy} onChange={e => { setProvider(e.target.value); setApiKey(''); setError(''); setMessage(''); }}>
          {Object.keys(KEY_LINKS).map(key => <option key={key} value={key}>{AI_PROVIDERS[key]}</option>)}
        </select></label>
        {connected && <p role="status">Connected · key ending in {connected.key_hint}</p>}
        <label className="v2-field"><span>{connected ? 'Replacement API key' : 'API key'}</span><input type="password" autoComplete="off" spellCheck="false" required maxLength={4096} value={apiKey} disabled={busy || !integrationsEnabled} onChange={e => setApiKey(e.target.value)}/></label>
        <a href={KEY_LINKS[provider]} target="_blank" rel="noreferrer">Create a key in {AI_PROVIDERS[provider]}</a>
        <p className="v2-muted">Your key is encrypted on the server. Selected workspace context is sent to this provider when you run AI. API usage is billed to your provider account.</p>
        {!integrationsEnabled && <p role="status">Hosted AI connections require Firebase sign-in. Local Ollama is available in the development model picker.</p>}
        {error && <p role="alert" className="v2-error">{error}</p>}
        {message && <p role="status">{message}</p>}
        <div className="v2-actions">
          <button className="v2-button" disabled={busy || !apiKey.trim() || !integrationsEnabled}>{busy ? 'Working…' : connected ? 'Verify & replace key' : 'Verify & connect'}</button>
          {connected && <button type="button" className="v2-button secondary" disabled={busy} onClick={() => run(async () => { await disconnectAIProvider(provider); setMessage(`${AI_PROVIDERS[provider]} disconnected from SPCC.`); })}>Disconnect provider</button>}
          <button type="button" className="v2-button secondary" disabled={busy} onClick={() => { setApiKey(''); onClose(); }}>Close</button>
        </div>
      </form>
    </DialogContent>
  </Dialog>;
}
